/**
 * Debaixo da foto da cliente: o que a IA achou no estoque do ERP. Só a
 * vendedora vê. Nunca diz que a loja não tem — só que não achou.
 */
import type { CorrespondenciaProduto, IdentificacaoProduto } from "@ydchat/shared";
import { ROTULO_NIVEL, rotuloConfianca } from "@ydchat/shared";
import { Loader2, RefreshCw, ScanSearch, Send, ShoppingBag } from "lucide-react";
import { Selo } from "@/components/ui/selo";
import { formatarHora, formatarMoeda } from "@/lib/formatar";
import { useAnalisarFoto } from "@/lib/visao";
import { useEnviarProduto } from "@/lib/vitrine";
import { Foto } from "./venda";

export function Identificacao({
  conversaId,
  mensagemId,
  identificacao: id,
  analisando,
  onBuscarNaVenda,
}: {
  conversaId: string;
  mensagemId: string;
  identificacao: IdentificacaoProduto | null;
  analisando: boolean;
  onBuscarNaVenda: (busca: string) => void;
}) {
  const analisar = useAnalisarFoto(conversaId);
  const pedir = () => analisar.mutate(mensagemId);

  if (analisando) {
    return (
      <Moldura>
        <p className="flex items-center gap-2 text-[13px] text-gold-800" aria-live="polite">
          <Loader2 size={14} className="animate-spin" /> Procurando esta peça no estoque…
        </p>
      </Moldura>
    );
  }
  if (!id) {
    return (
      <div className="mb-1 pl-1">
        <button
          onClick={pedir}
          className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[13px] font-semibold text-gold-700 hover:bg-gold-50"
        >
          <ScanSearch size={15} strokeWidth={1.75} /> Procurar esta peça no estoque
        </button>
      </div>
    );
  }

  const novamente = (
    <button
      onClick={pedir}
      aria-label="Analisar de novo"
      title="Analisar de novo"
      className="rounded-md p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
    >
      <RefreshCw size={14} strokeWidth={1.75} />
    </button>
  );

  if (id.erro || !id.descricao) {
    return (
      <Moldura>
        <div className="flex items-center justify-between gap-2 text-[13px] text-neutral-600">
          <span>{id.erro ?? "Não consegui analisar esta foto."}</span>
          <button onClick={pedir} className="font-semibold text-gold-700 hover:underline">
            Tentar de novo
          </button>
        </div>
      </Moldura>
    );
  }

  const d = id.descricao;
  return (
    <Moldura>
      <div className="flex items-start gap-2">
        <ScanSearch size={16} strokeWidth={1.75} className="mt-0.5 shrink-0 text-gold-600" />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-neutral-900">
            {d.comprovante
              ? "Parece um comprovante de pagamento: confira no banco e no ERP"
              : !d.ehProduto
                ? "Não parece ser uma peça da loja"
                : id.correspondencias[0]?.nivel === "exata"
                  ? "Possível correspondência no estoque"
                  : id.correspondencias.length
                    ? "Parecidos no estoque"
                    : "Não achei no estoque"}
          </p>
          <p className="text-xs text-neutral-500">{d.resumo}</p>
        </div>
        {novamente}
      </div>

      {d.ehProduto && !id.correspondencias.length && (
        <p className="mt-2 text-[13px] text-neutral-600">
          Pode ser recém-chegada ou estar com outro nome no ERP.{" "}
          <button
            onClick={() => onBuscarNaVenda(d.tipo)}
            className="font-semibold text-gold-700 hover:underline"
          >
            Buscar “{d.tipo}” na venda
          </button>
        </p>
      )}

      {!!id.correspondencias.length && (
        <ul className="mt-2 divide-y divide-neutral-100">
          {id.correspondencias.map((c) => (
            <Linha
              key={c.produto.id}
              c={c}
              conversaId={conversaId}
              onBuscarNaVenda={onBuscarNaVenda}
            />
          ))}
        </ul>
      )}
      {!!id.correspondencias.length && (
        <p className="mt-1.5 text-[11px] text-neutral-500">
          Estoque às {formatarHora(id.criadoEm)}. Confira na venda antes de confirmar à cliente.
        </p>
      )}
    </Moldura>
  );
}

function Moldura({ children }: { children: React.ReactNode }) {
  return (
    <section
      aria-label="Produto da foto"
      className="mb-1 w-[min(100%,26rem)] rounded-xl border border-gold-200 bg-white p-2.5 shadow-xs"
    >
      {children}
    </section>
  );
}

function Linha({
  c,
  conversaId,
  onBuscarNaVenda,
}: {
  c: CorrespondenciaProduto;
  conversaId: string;
  onBuscarNaVenda: (busca: string) => void;
}) {
  const p = c.produto;
  const enviar = useEnviarProduto(conversaId);
  const confianca = rotuloConfianca(c.confianca);
  const preco = p.variacoes.length ? Math.min(...p.variacoes.map((v) => v.preco)) : null;
  return (
    <li className="flex gap-2.5 py-2">
      <Foto url={p.fotoUrl ?? p.variacoes.find((v) => v.fotoUrl)?.fotoUrl ?? null} nome={p.nome} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <Selo
            tom={c.nivel === "exata" ? "sucesso" : c.nivel === "semelhante" ? "dourado" : "neutro"}
          >
            {ROTULO_NIVEL[c.nivel]}
          </Selo>
          <span className="text-[11px] text-neutral-500" title={`${c.confianca}%`}>
            confiança {confianca.toLowerCase()}
          </span>
        </div>
        <p className="mt-0.5 truncate text-sm font-semibold text-neutral-900">{p.nome}</p>
        {preco !== null && (
          <p className="font-display text-sm font-bold text-neutral-900">{formatarMoeda(preco)}</p>
        )}
        <div className="mt-1 flex flex-wrap gap-1">
          {p.variacoes.map((v) => (
            <span
              key={v.id}
              className={
                v.estoqueDisponivel > 0
                  ? "rounded border border-neutral-200 px-1.5 py-0.5 text-[11px] font-semibold text-neutral-700"
                  : "rounded border border-neutral-100 px-1.5 py-0.5 text-[11px] text-neutral-400 line-through"
              }
            >
              {[v.tamanho, v.cor].filter(Boolean).join(" · ") || "Único"} ({v.estoqueDisponivel})
            </span>
          ))}
        </div>
        <p className="mt-1 text-xs text-neutral-500">{c.motivo}</p>
      </div>
      <div className="flex flex-col gap-1 self-start">
        <button
          onClick={() => enviar.mutate(p.id)}
          disabled={enviar.isPending}
          aria-label={`Enviar ${p.nome} à cliente`}
          title={
            p.urlLoja
              ? "Enviar à cliente (foto, preço e link do site)"
              : "Enviar à cliente (foto e preço)"
          }
          className="rounded-md p-1.5 text-gold-700 hover:bg-gold-50 disabled:opacity-50"
        >
          <Send size={16} strokeWidth={1.75} />
        </button>
        <button
          onClick={() => onBuscarNaVenda(p.nome)}
          aria-label={`Ver ${p.nome} na venda`}
          title="Ver na venda"
          className="rounded-md p-1.5 text-gold-700 hover:bg-gold-50"
        >
          <ShoppingBag size={16} strokeWidth={1.75} />
        </button>
      </div>
    </li>
  );
}
